import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuthSession, clearAuthSession } from '../services/api';

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const isDarkHeroPage =
    [
      '/services',
      '/therapists',
      '/digistore',
      '/products',
      '/appointment',
      '/team',
      '/gallery',
      '/faq',
      '/podcasts',
      '/events',
      '/community',
      '/client-guide',
      '/mhp-guide',
      '/cart',
      '/checkout',
      '/cases',
      '/register/physiotherapist',
      '/register/therapist',
      '/forgot-password',
      '/specialized-service',
    ].includes(location.pathname) ||
    location.pathname.startsWith('/session-payment/');
  const [isMobileNavActive, setIsMobileNavActive] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [authSession, setAuthSession] = useState(() => getAuthSession());

  useEffect(() => {
    const updateAuth = () => {
      setAuthSession(getAuthSession());
    };

    updateAuth();
    window.addEventListener('storage', updateAuth);
    window.addEventListener('auth-changed', updateAuth);
    return () => {
      window.removeEventListener('storage', updateAuth);
      window.removeEventListener('auth-changed', updateAuth);
    };
  }, [location.pathname]);

  const handleLogout = () => {
    clearAuthSession();
    setAuthSession(null);
    navigate('/login');
  };

  const getDashboardPath = () => {
    if (!authSession?.user) return '/login';
    if (authSession.user.role === 'admin') return '/admin-dashboard';
    if (authSession.user.role === 'physiotherapist') return '/physio-dashboard';
    return '/client-dashboard';
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 130);

      const scrollToTopButton = document.querySelector('.scroll-to-top');
      if (scrollToTopButton) {
        scrollToTopButton.style.display = window.scrollY > 100 ? 'inline-block' : 'none';
        const mascot = document.querySelector('.mascot-container');
        if (mascot && mascot.offsetHeight > 0) {
          scrollToTopButton.style.bottom = `${mascot.offsetHeight + 35}px`;
        } else {
          scrollToTopButton.style.bottom = '35px';
        }
      }
    };

    const handleScrollToTop = (e) => {
      if (e.target.closest('.scroll-to-top')) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleScrollToTop);

    // Initialize WOW if available
    setTimeout(() => {
      if (window.WOW) {
        try { new window.WOW().init(); } catch (e) { }
      }
    }, 500);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleScrollToTop);
    };
  }, []);

  // Close mobile nav on route change
  useEffect(() => {
    setIsMobileNavActive(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  // Lock background scroll when mobile nav is open
  useEffect(() => {
    if (isMobileNavActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileNavActive]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    // { name: 'DigiStore', path: '/digistore' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const renderNavList = () => (
    <ul className="main-menu__list">
      {navLinks.map((link) => {
        if (link.path === '/services') {
          return (
            <li key={link.path} className={`pm-nav-dropdown-item ${location.pathname === '/services' ? 'current' : ''}`}>
              <Link to="/services" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {link.name} <i className="fa-solid fa-chevron-down" style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.7 }}></i>
              </Link>
              <div className="pm-mega-menu">
                <div className="pm-mega-grid">
                  <div>
                    <div className="pm-mega-col-title" style={{ color: '#7c3aed' }}>
                      <span>🧠</span> Psychological & Mental Health
                    </div>
                    <ul className="pm-mega-list">
                      <li><Link to="/services#psychological-mental-health"><i className="fa-solid fa-hands-holding-child" style={{ color: '#7c3aed', width: '16px' }}></i> Counselling Psychologist</Link></li>
                      <li><Link to="/services#psychological-mental-health"><i className="fa-solid fa-brain" style={{ color: '#7c3aed', width: '16px' }}></i> Clinical Psychologist</Link></li>
                      <li><Link to="/services#psychological-mental-health"><i className="fa-solid fa-user-doctor" style={{ color: '#7c3aed', width: '16px' }}></i> Psychiatrist</Link></li>
                      <li><Link to="/services#psychological-mental-health"><i className="fa-solid fa-wheelchair-move" style={{ color: '#7c3aed', width: '16px' }}></i> Rehabilitation Psychologist</Link></li>
                      <li><Link to="/services#psychological-mental-health"><i className="fa-solid fa-child-reaching" style={{ color: '#7c3aed', width: '16px' }}></i> Child Psychologist</Link></li>
                      <li><Link to="/services#psychological-mental-health"><i className="fa-solid fa-person-cane" style={{ color: '#7c3aed', width: '16px' }}></i> Geriatric Psychologist</Link></li>
                      <li><Link to="/services#psychological-mental-health"><i className="fa-solid fa-ribbon" style={{ color: '#7c3aed', width: '16px' }}></i> Psycho-Oncologist</Link></li>
                    </ul>
                  </div>

                  <div>
                    <div className="pm-mega-col-title" style={{ color: '#059669' }}>
                      <span>🌱</span> Development & Functional
                    </div>
                    <ul className="pm-mega-list">
                      <li><Link to="/services#development-functional"><i className="fa-solid fa-graduation-cap" style={{ color: '#059669', width: '16px' }}></i> Special Educator</Link></li>
                      <li><Link to="/services#development-functional"><i className="fa-solid fa-hand-holding-medical" style={{ color: '#059669', width: '16px' }}></i> Occupational Therapist</Link></li>
                      <li><Link to="/services#development-functional"><i className="fa-solid fa-comment-dots" style={{ color: '#059669', width: '16px' }}></i> Speech Therapist</Link></li>
                    </ul>
                  </div>

                  <div>
                    <div className="pm-mega-col-title" style={{ color: '#d97706' }}>
                      <span>🎯</span> Performance, Work & Education
                    </div>
                    <ul className="pm-mega-list">
                      <li><Link to="/services#performance-work-education"><i className="fa-solid fa-person-running" style={{ color: '#d97706', width: '16px' }}></i> Sports Psychologist</Link></li>
                      <li><Link to="/services#performance-work-education"><i className="fa-solid fa-compass" style={{ color: '#d97706', width: '16px' }}></i> Career Counsellor</Link></li>
                      <li><Link to="/services#performance-work-education"><i className="fa-solid fa-briefcase" style={{ color: '#d97706', width: '16px' }}></i> Organisational Psychologist</Link></li>
                      <li><Link to="/services#performance-work-education"><i className="fa-solid fa-bullseye" style={{ color: '#d97706', width: '16px' }}></i> Coach</Link></li>
                    </ul>
                  </div>
                </div>

                <div className="pm-mega-footer">
                  <span style={{ color: '#64748b' }}>Looking for personalized care?</span>
                  <Link to="/services">
                    Explore All 14 Specialities & Matcher →
                  </Link>
                </div>
              </div>
            </li>
          );
        }

        return (
          <li key={link.path} className={location.pathname === link.path ? 'current' : ''}>
            <Link to={link.path}>{link.name}</Link>
          </li>
        );
      })}
    </ul>
  );

  const renderRightActions = () => (
    <div className="main-menu-two__right">
      {authSession?.token ? (
        <>
          <Link
            to={getDashboardPath()}
            className="thm-btn thm-btn--header pm-header-action-btn"
            style={{
              marginRight: '10px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              color: '#fff',
            }}
          >
            🧑‍💻 Dashboard
          </Link>
          <button
            onClick={handleLogout}
            className="thm-btn thm-btn--header pm-header-logout-btn"
            style={{
              background: 'transparent',
              border: '2px solid #ef4444',
              color: '#ef4444',
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </>
      ) : (
        <>
          <Link
            to="/login"
            className="thm-btn thm-btn--header pm-header-login-btn"
            style={{
              marginRight: '10px',
              background: 'transparent',
              border: '2px solid #7c3aed',
              color: '#7c3aed',
            }}
          >
            Login
          </Link>
          <Link
            to="/register"
            className="thm-btn thm-btn--header pm-header-register-btn"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              color: '#fff',
            }}
          >
            Register Now
          </Link>
        </>
      )}
      <button
        type="button"
        className="pm-mobile-nav__toggler"
        onClick={() => setIsMobileNavActive(true)}
        aria-label="Toggle mobile menu"
        title="Open Navigation Menu"
      >
        <i className="fa-solid fa-bars"></i>
      </button>
    </div>
  );

  return (
    <>
      <style>
        {`
          /* =========================================================
             TRANSPARENT HEADER ACROSS ALL PAGES
             ========================================================= */
          .main-header-two {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            background: transparent !important;
            border: none !important;
            border-bottom: none !important;
            box-shadow: none !important;
            z-index: 999 !important;
            transition: all 0.3s ease;
          }
          .main-header-two .main-menu,
          .main-header-two .main-menu-two,
          .main-header-two .main-menu-two__wrapper,
          .main-header-two .main-menu-two__wrapper-inner,
          .stricky-header .main-menu,
          .stricky-header .main-menu-two,
          .stricky-header .main-menu-two__wrapper,
          .stricky-header .main-menu-two__wrapper-inner {
            background: transparent !important;
            background-color: transparent !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            border: none !important;
            border-bottom: none !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .stricky-header {
            z-index: 9999 !important;
          }
          .stricky-header .main-menu__list > li > a {
            color: #1e1b4b !important;
            text-shadow: none !important;
          }
          .stricky-header .main-menu__list > li > a:hover,
          .stricky-header .main-menu__list > li.current > a {
            color: #7c3aed !important;
          }

          /* Light Hero Pages (Homepage Video, About, Blog, Contact, Pricing) */
          .main-header-two--light-hero .main-menu__list > li > a {
            color: #1e1b4b !important;
            text-shadow: 0 1px 3px rgba(255, 255, 255, 0.85), 0 0 12px rgba(255, 255, 255, 0.6);
          }
          .main-header-two--light-hero .main-menu__list > li > a:hover {
            color: #7c3aed !important;
          }
          .main-header-two--light-hero .pm-header-login-btn {
            background: rgba(255, 255, 255, 0.65) !important;
            backdrop-filter: blur(8px) !important;
            -webkit-backdrop-filter: blur(8px) !important;
            border: 2px solid #7c3aed !important;
            color: #7c3aed !important;
          }
          .main-header-two--light-hero .pm-header-login-btn:hover {
            background: #7c3aed !important;
            color: #ffffff !important;
          }
          .main-header-two--light-hero .pm-mobile-nav__toggler {
            background: rgba(255, 255, 255, 0.85) !important;
            backdrop-filter: blur(8px) !important;
            -webkit-backdrop-filter: blur(8px) !important;
            border: 1.5px solid rgba(124, 58, 237, 0.3) !important;
            color: #7c3aed !important;
          }

          /* Dark Hero Pages (Services, Team, FAQs, Events with dark banners) */
          .main-header-two--dark-hero .main-menu__list > li > a {
            color: #ffffff !important;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
          }
          .main-header-two--dark-hero .main-menu__list > li > a:hover {
            color: #c4b5fd !important;
          }
          .main-header-two--dark-hero .pm-header-login-btn {
            background: rgba(255, 255, 255, 0.12) !important;
            backdrop-filter: blur(8px) !important;
            -webkit-backdrop-filter: blur(8px) !important;
            border: 2px solid rgba(255, 255, 255, 0.8) !important;
            color: #ffffff !important;
          }
          .main-header-two--dark-hero .pm-header-login-btn:hover {
            background: #ffffff !important;
            color: #7c3aed !important;
          }
          .main-header-two--dark-hero .pm-mobile-nav__toggler {
            background: rgba(255, 255, 255, 0.15) !important;
            backdrop-filter: blur(8px) !important;
            -webkit-backdrop-filter: blur(8px) !important;
            border: 1.5px solid rgba(255, 255, 255, 0.3) !important;
            color: #ffffff !important;
          }
          .main-menu__list > li > a {
            position: relative;
            font-weight: 700;
            color: #2d2d4d !important;
            transition: color 0.3s ease;
            font-size: 1.05rem;
          }
          .main-menu__list > li > a:after {
            content: '';
            position: absolute;
            bottom: -5px;
            left: 0;
            width: 0;
            height: 2px;
            background: linear-gradient(90deg, #7c3aed, #4f46e5);
            transition: width 0.3s ease;
            border-radius: 2px;
          }
          .main-menu__list > li > a:hover:after { width: 100%; }
          .main-menu__list > li > a:hover { color: #7c3aed !important; }

          /* Dropdown Mega Menu for Services */
          .pm-nav-dropdown-item {
            position: relative;
          }

          .pm-mega-menu {
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%) translateY(12px);
            width: 780px;
            max-width: 92vw;
            background: #ffffff;
            border-radius: 20px;
            box-shadow: 0 20px 50px rgba(24, 17, 38, 0.16);
            border: 1.5px solid #ece4f8;
            padding: 24px;
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            transition: all 0.28s cubic-bezier(0.16, 1, 0.3, 1);
            z-index: 99999;
          }

          .pm-nav-dropdown-item:hover .pm-mega-menu {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
            transform: translateX(-50%) translateY(0);
          }

          .pm-mega-grid {
            display: grid;
            grid-template-columns: 1.15fr 1fr 1fr;
            gap: 18px;
            text-align: left;
          }

          .pm-mega-col-title {
            font-size: 12.5px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .pm-mega-list {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .pm-mega-list li {
            margin: 0;
            padding: 0;
          }

          .pm-mega-list li a {
            display: flex !important;
            align-items: center;
            gap: 8px;
            font-size: 13px !important;
            font-weight: 600 !important;
            color: #4b3e64 !important;
            text-decoration: none !important;
            padding: 5px 8px !important;
            border-radius: 8px;
            transition: all 0.2s ease;
            white-space: nowrap;
          }

          .pm-mega-list li a:after {
            display: none !important;
          }

          .pm-mega-list li a:hover {
            background: #f6f0ff;
            color: #7c4bb9 !important;
            transform: translateX(3px);
          }

          .pm-mega-footer {
            margin-top: 16px;
            padding-top: 12px;
            border-top: 1px solid #f0eaf8;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 13px;
          }

          .pm-mega-footer a {
            font-weight: 700 !important;
            color: #7c4bb9 !important;
            text-decoration: none !important;
            transition: transform 0.2s ease;
          }

          .pm-mega-footer a:after {
            display: none !important;
          }

          .pm-mega-footer a:hover {
            transform: translateX(3px);
          }

          .thm-btn--header {
            padding: 10px 24px !important;
            font-size: 0.95rem !important;
            font-weight: 700 !important;
            border-radius: 12px !important;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
            box-shadow: 0 4px 12px rgba(124, 58, 237, 0.15) !important;
          }
          .thm-btn--header:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(124, 58, 237, 0.25) !important;
          }
          .stricky-fixed {
            background: rgba(255, 255, 255, 0.95) !important;
            backdrop-filter: blur(20px) !important;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
          }
          .main-menu-two__logo img { transition: transform 0.3s ease; }
          .main-menu-two__logo:hover img { transform: scale(1.03); }

          .page-header {
            position: relative;
            padding: 120px 0 90px !important;
            overflow: hidden;
            background-color: #1a1a2e;
          }
          .page-header__bg {
            filter: brightness(0.7) contrast(1.1);
            transform: scale(1.05);
            transition: transform 10s linear;
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background-size: cover;
            background-position: center;
          }
          .page-header:hover .page-header__bg { transform: scale(1.15); }
          .page-header:before {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: linear-gradient(180deg, rgba(124, 58, 237, 0.4) 0%, rgba(31, 41, 55, 0.85) 100%);
            z-index: 1;
          }
          .page-header__inner { position: relative; z-index: 2; text-align: center; }
          .page-header__title {
            font-size: 4rem !important;
            font-weight: 900 !important;
            color: #ffffff !important;
            text-shadow: 0 10px 30px rgba(0,0,0,0.3);
            letter-spacing: -1px;
            margin-bottom: 20px !important;
            animation: fadeInUp 0.8s ease-out;
          }
          .thm-breadcrumb {
            display: inline-flex !important;
            align-items: center;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            padding: 8px 24px !important;
            border-radius: 50px;
            border: 1px solid rgba(255, 255, 255, 0.2);
            animation: fadeInUp 1s ease-out;
          }
          .thm-breadcrumb li, .thm-breadcrumb li a, .thm-breadcrumb li span {
            color: rgba(255, 255, 255, 0.9) !important;
            font-weight: 600 !important;
            font-size: 0.95rem !important;
          }
          .thm-breadcrumb li a:hover { color: #7c3aed !important; }
          .page-header:after {
            content: '🌿';
            position: absolute;
            top: 20%; right: 10%;
            font-size: 3rem;
            opacity: 0.1;
            filter: blur(2px);
            animation: float-y 6s infinite ease-in-out;
            z-index: 1;
          }
          @keyframes float-y { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }

          /* Mobile Navigation Drawer Styles */
          .mobile-nav__wrapper {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 999999 !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
            transition: opacity 0.3s ease, visibility 0.3s ease !important;
          }
          .mobile-nav__wrapper.expanded,
          .mobile-nav__wrapper.mobile-nav__wrapper--active {
            visibility: visible !important;
            opacity: 1 !important;
            pointer-events: auto !important;
          }
          .mobile-nav__overlay {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            background: rgba(15, 10, 28, 0.72) !important;
            backdrop-filter: blur(6px) !important;
            -webkit-backdrop-filter: blur(6px) !important;
            cursor: pointer !important;
          }
          .mobile-nav__content {
            position: relative !important;
            width: 320px !important;
            max-width: 86vw !important;
            height: 100% !important;
            background: linear-gradient(180deg, #181126 0%, #0f0a1c 100%) !important;
            border-right: 1px solid rgba(124, 58, 237, 0.25) !important;
            box-shadow: 15px 0 50px rgba(0, 0, 0, 0.6) !important;
            display: flex !important;
            flex-direction: column !important;
            padding: 22px 18px !important;
            overflow-y: auto !important;
            z-index: 10 !important;
            transform: translateX(-100%) !important;
            transition: transform 0.32s cubic-bezier(0.16, 1, 0.3, 1) !important;
          }
          .mobile-nav__wrapper.expanded .mobile-nav__content,
          .mobile-nav__wrapper.mobile-nav__wrapper--active .mobile-nav__content {
            transform: translateX(0) !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          .pm-mobile-nav-top {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            padding-bottom: 16px !important;
            margin-bottom: 16px !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
          }
          .mobile-nav__close {
            position: static !important;
            width: 38px !important;
            height: 38px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: rgba(255, 255, 255, 0.08) !important;
            border: 1px solid rgba(255, 255, 255, 0.12) !important;
            border-radius: 50% !important;
            color: #cbd5e1 !important;
            cursor: pointer !important;
            font-size: 15px !important;
            transition: all 0.2s ease !important;
          }
          .mobile-nav__close:hover {
            background: #7c3aed !important;
            color: #ffffff !important;
            border-color: #7c3aed !important;
            transform: rotate(90deg) !important;
          }
          .pm-mobile-menu-list {
            list-style: none !important;
            padding: 0 !important;
            margin: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 6px !important;
          }
          .pm-mobile-menu-item > a {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            padding: 12px 14px !important;
            color: #f1f5f9 !important;
            font-size: 15px !important;
            font-weight: 600 !important;
            text-decoration: none !important;
            border-radius: 10px !important;
            transition: all 0.2s ease !important;
            background: transparent !important;
          }
          .pm-mobile-menu-item > a:hover,
          .pm-mobile-menu-item.active > a {
            background: rgba(124, 58, 237, 0.18) !important;
            color: #c4b5fd !important;
          }
          .pm-mobile-services-accordion {
            background: rgba(255, 255, 255, 0.03) !important;
            border: 1px solid rgba(124, 58, 237, 0.15) !important;
            border-radius: 12px !important;
            padding: 12px !important;
            margin: 6px 0 10px !important;
          }
          .pm-mobile-service-group {
            margin-bottom: 12px !important;
          }
          .pm-mobile-group-title {
            font-size: 11.5px !important;
            font-weight: 800 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            margin-bottom: 6px !important;
          }
          .pm-mobile-sublist {
            list-style: none !important;
            padding: 0 0 0 10px !important;
            margin: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 4px !important;
            border-left: 1.5px solid rgba(255, 255, 255, 0.08) !important;
          }
          .pm-mobile-sublist li a {
            color: #94a3b8 !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            text-decoration: none !important;
            display: block !important;
            padding: 3px 0 !important;
            transition: color 0.2s ease !important;
          }
          .pm-mobile-sublist li a:hover {
            color: #ffffff !important;
          }
          .pm-mobile-auth-section {
            padding: 18px 0 16px !important;
            border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
            margin-top: 16px !important;
          }
          .pm-mobile-contact-footer {
            margin-top: auto !important;
            padding-top: 16px !important;
            border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
          }
          .pm-mobile-contact-item {
            color: #94a3b8 !important;
            font-size: 13px !important;
            text-decoration: none !important;
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
          }
          .pm-mobile-contact-item:hover {
            color: #c4b5fd !important;
          }
          .pm-mobile-socials {
            display: flex !important;
            gap: 10px !important;
            margin-top: 8px !important;
          }
          .pm-mobile-socials a {
            width: 32px !important;
            height: 32px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: rgba(255, 255, 255, 0.06) !important;
            border-radius: 8px !important;
            color: #cbd5e1 !important;
            font-size: 13px !important;
            transition: all 0.2s ease !important;
          }
          .pm-mobile-socials a:hover {
            background: #7c3aed !important;
            color: #fff !important;
          }

          /* Header & Toggler Responsive Styles */
          .pm-mobile-nav__toggler {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 44px !important;
            height: 44px !important;
            border-radius: 12px !important;
            background: #f5f3ff !important;
            border: 1.5px solid #ddd6fe !important;
            color: #7c3aed !important;
            cursor: pointer !important;
            font-size: 19px !important;
            transition: all 0.25s ease !important;
            flex-shrink: 0 !important;
            box-shadow: 0 2px 6px rgba(124, 58, 237, 0.08) !important;
          }
          .pm-mobile-nav__toggler:hover,
          .pm-mobile-nav__toggler:active {
            background: #7c3aed !important;
            color: #ffffff !important;
            border-color: #7c3aed !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25) !important;
          }
          @media (min-width: 1200px) {
            .pm-mobile-nav__toggler {
              display: none !important;
            }
          }
          @media (max-width: 1199px) {
            .main-menu-two__main-menu-box {
              display: none !important;
            }
            .main-menu-two__wrapper-inner {
              padding: 0 20px !important;
            }
          }
          @media (max-width: 767px) {
            .main-menu-two__right {
              display: flex !important;
              align-items: center !important;
              justify-content: flex-end !important;
              gap: 8px !important;
            }
            .main-menu-two__wrapper-inner {
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              padding: 10px 16px !important;
              box-shadow: none !important;
            }
            .main-menu-two__logo img {
              height: 55px !important;
              max-width: 160px !important;
              object-fit: contain !important;
            }
            .pm-header-register-btn,
            .pm-header-logout-btn {
              display: none !important;
            }
            .pm-header-login-btn,
            .pm-header-action-btn {
              padding: 8px 14px !important;
              font-size: 0.85rem !important;
              margin-right: 4px !important;
            }
          }
          @media (max-width: 480px) {
            .main-menu-two__logo img {
              height: 48px !important;
              max-width: 140px !important;
            }
            .pm-header-login-btn,
            .pm-header-action-btn {
              padding: 7px 11px !important;
              font-size: 0.82rem !important;
            }
            .pm-mobile-nav__toggler {
              width: 40px !important;
              height: 40px !important;
              font-size: 18px !important;
            }
          }
        `}
      </style>

      {/* Sticky Header */}
      <div className={`stricky-header stricked-menu main-menu ${isSticky ? 'stricky-fixed' : ''}`}>
        <div className="sticky-header__content">
          <nav className="main-menu main-menu-two">
            <div className="main-menu-two__wrapper">
              <div className="main-menu-two__wrapper-inner">
                <div className="main-menu-two__logo">
                  <Link to="/"><img src="/assets/images/PsychMitra.png" alt="" style={{ height: '65px' }} /></Link>
                </div>
                <div className="main-menu-two__main-menu-box">
                  {renderNavList()}
                </div>
                {renderRightActions()}
              </div>
            </div>
          </nav>
        </div>
      </div>

      <header className={`main-header-two main-header-two--transparent ${isDarkHeroPage ? 'main-header-two--dark-hero' : 'main-header-two--light-hero'}`}>
        <nav className="main-menu main-menu-two">
          <div className="main-menu-two__wrapper">
            <div className="main-menu-two__wrapper-inner">
              <div className="main-menu-two__logo">
                <Link to="/"><img src="/assets/images/PsychMitra.png" alt="" style={{ height: '85px' }} /></Link>
              </div>
              <div className="main-menu-two__main-menu-box">
                {renderNavList()}
              </div>
              {renderRightActions()}
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Nav */}
      <div className={`mobile-nav__wrapper ${isMobileNavActive ? 'expanded mobile-nav__wrapper--active' : ''}`}>
        <div className="mobile-nav__overlay" onClick={() => setIsMobileNavActive(false)}></div>
        <div className="mobile-nav__content">
          <div className="pm-mobile-nav-top">
            <Link to="/" onClick={() => setIsMobileNavActive(false)} className="pm-mobile-nav-logo">
              <img src="/assets/images/PsychMitra.png" alt="PsychMitra Logo" style={{ maxHeight: '55px', width: 'auto' }} />
            </Link>
            <button
              type="button"
              className="mobile-nav__close"
              onClick={() => setIsMobileNavActive(false)}
              aria-label="Close menu"
            >
              <i className="fa fa-times"></i>
            </button>
          </div>

          <div className="mobile-nav__container">
            <ul className="pm-mobile-menu-list">
              <li className={`pm-mobile-menu-item ${location.pathname === '/' ? 'active' : ''}`}>
                <Link to="/" onClick={() => setIsMobileNavActive(false)}>
                  <span><i className="fa-solid fa-house" style={{ width: '20px', color: '#a78bfa' }}></i> Home</span>
                  <i className="fa-solid fa-chevron-right" style={{ fontSize: '11px', opacity: 0.5 }}></i>
                </Link>
              </li>

              <li className={`pm-mobile-menu-item ${location.pathname === '/about' ? 'active' : ''}`}>
                <Link to="/about" onClick={() => setIsMobileNavActive(false)}>
                  <span><i className="fa-solid fa-circle-info" style={{ width: '20px', color: '#a78bfa' }}></i> About</span>
                  <i className="fa-solid fa-chevron-right" style={{ fontSize: '11px', opacity: 0.5 }}></i>
                </Link>
              </li>

              <li className={`pm-mobile-menu-item ${location.pathname === '/services' ? 'active' : ''}`}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Link
                    to="/services"
                    onClick={() => setIsMobileNavActive(false)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '12px 14px' }}
                  >
                    <span><i className="fa-solid fa-hand-holding-heart" style={{ width: '20px', color: '#a78bfa' }}></i> Services</span>
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMobileServicesOpen(!isMobileServicesOpen);
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      border: 'none',
                      color: '#c4b5fd',
                      width: '38px',
                      height: '38px',
                      borderRadius: '8px',
                      marginRight: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    aria-label="Toggle services categories"
                  >
                    <i className={`fa-solid fa-chevron-${isMobileServicesOpen ? 'up' : 'down'}`} style={{ fontSize: '12px' }}></i>
                  </button>
                </div>

                {isMobileServicesOpen && (
                  <div className="pm-mobile-services-accordion">
                    <div className="pm-mobile-service-group">
                      <div className="pm-mobile-group-title" style={{ color: '#c4b5fd' }}>
                        🧠 Psychological & Mental Health
                      </div>
                      <ul className="pm-mobile-sublist">
                        <li><Link to="/services#psychological-mental-health" onClick={() => setIsMobileNavActive(false)}>Counselling Psychologist</Link></li>
                        <li><Link to="/services#psychological-mental-health" onClick={() => setIsMobileNavActive(false)}>Clinical Psychologist</Link></li>
                        <li><Link to="/services#psychological-mental-health" onClick={() => setIsMobileNavActive(false)}>Psychiatrist</Link></li>
                        <li><Link to="/services#psychological-mental-health" onClick={() => setIsMobileNavActive(false)}>Rehabilitation Psychologist</Link></li>
                        <li><Link to="/services#psychological-mental-health" onClick={() => setIsMobileNavActive(false)}>Child Psychologist</Link></li>
                        <li><Link to="/services#psychological-mental-health" onClick={() => setIsMobileNavActive(false)}>Geriatric Psychologist</Link></li>
                        <li><Link to="/services#psychological-mental-health" onClick={() => setIsMobileNavActive(false)}>Psycho-Oncologist</Link></li>
                      </ul>
                    </div>

                    <div className="pm-mobile-service-group">
                      <div className="pm-mobile-group-title" style={{ color: '#6ee7b7' }}>
                        🌱 Development & Functional
                      </div>
                      <ul className="pm-mobile-sublist">
                        <li><Link to="/services#development-functional" onClick={() => setIsMobileNavActive(false)}>Special Educator</Link></li>
                        <li><Link to="/services#development-functional" onClick={() => setIsMobileNavActive(false)}>Occupational Therapist</Link></li>
                        <li><Link to="/services#development-functional" onClick={() => setIsMobileNavActive(false)}>Speech Therapist</Link></li>
                      </ul>
                    </div>

                    <div className="pm-mobile-service-group">
                      <div className="pm-mobile-group-title" style={{ color: '#fcd34d' }}>
                        🎯 Performance, Work & Education
                      </div>
                      <ul className="pm-mobile-sublist">
                        <li><Link to="/services#performance-work-education" onClick={() => setIsMobileNavActive(false)}>Sports Psychologist</Link></li>
                        <li><Link to="/services#performance-work-education" onClick={() => setIsMobileNavActive(false)}>Career Counsellor</Link></li>
                        <li><Link to="/services#performance-work-education" onClick={() => setIsMobileNavActive(false)}>Organisational Psychologist</Link></li>
                        <li><Link to="/services#performance-work-education" onClick={() => setIsMobileNavActive(false)}>Coach</Link></li>
                      </ul>
                    </div>

                    <Link
                      to="/services"
                      onClick={() => setIsMobileNavActive(false)}
                      style={{
                        display: 'block',
                        padding: '8px 12px',
                        background: 'rgba(124,58,237,0.15)',
                        borderRadius: '8px',
                        color: '#c4b5fd',
                        fontSize: '12px',
                        fontWeight: 700,
                        marginTop: '8px',
                        textAlign: 'center'
                      }}
                    >
                      View All 14 Specialities →
                    </Link>
                  </div>
                )}
              </li>

              <li className={`pm-mobile-menu-item ${location.pathname === '/blog' ? 'active' : ''}`}>
                <Link to="/blog" onClick={() => setIsMobileNavActive(false)}>
                  <span><i className="fa-solid fa-newspaper" style={{ width: '20px', color: '#a78bfa' }}></i> Blog</span>
                  <i className="fa-solid fa-chevron-right" style={{ fontSize: '11px', opacity: 0.5 }}></i>
                </Link>
              </li>

              <li className={`pm-mobile-menu-item ${location.pathname === '/contact' ? 'active' : ''}`}>
                <Link to="/contact" onClick={() => setIsMobileNavActive(false)}>
                  <span><i className="fa-solid fa-envelope" style={{ width: '20px', color: '#a78bfa' }}></i> Contact</span>
                  <i className="fa-solid fa-chevron-right" style={{ fontSize: '11px', opacity: 0.5 }}></i>
                </Link>
              </li>
            </ul>

            {/* Auth Buttons in Mobile Menu */}
            <div className="pm-mobile-auth-section">
              {authSession?.token ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', fontSize: '13px', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
                    <span>{authSession?.user?.name || authSession?.user?.email || 'Logged In'}</span>
                    <span style={{ marginLeft: 'auto', textTransform: 'capitalize', fontSize: '11px', background: 'rgba(124,58,237,0.3)', padding: '2px 8px', borderRadius: '10px', color: '#c4b5fd' }}>
                      {authSession?.user?.role || 'user'}
                    </span>
                  </div>
                  <Link
                    to={getDashboardPath()}
                    className="thm-btn"
                    style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                      color: '#fff',
                      textAlign: 'center',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      fontWeight: 700
                    }}
                    onClick={() => setIsMobileNavActive(false)}
                  >
                    🧑‍💻 Open Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      setIsMobileNavActive(false);
                      handleLogout();
                    }}
                    className="thm-btn"
                    style={{
                      background: 'transparent',
                      border: '1.5px solid #ef4444',
                      color: '#ef4444',
                      cursor: 'pointer',
                      textAlign: 'center',
                      padding: '10px 20px',
                      borderRadius: '10px',
                      fontWeight: 600
                    }}
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Link
                    to="/login"
                    className="thm-btn"
                    style={{
                      background: 'transparent',
                      border: '1.5px solid #7c3aed',
                      color: '#c4b5fd',
                      textAlign: 'center',
                      padding: '11px 20px',
                      borderRadius: '10px',
                      fontWeight: 600
                    }}
                    onClick={() => setIsMobileNavActive(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="thm-btn"
                    style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                      color: '#fff',
                      textAlign: 'center',
                      padding: '11px 20px',
                      borderRadius: '10px',
                      fontWeight: 700
                    }}
                    onClick={() => setIsMobileNavActive(false)}
                  >
                    Register Now
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="pm-mobile-contact-footer">
            <a href="mailto:help@psychmitra.com" className="pm-mobile-contact-item">
              <i className="fa-solid fa-envelope"></i> help@psychmitra.com
            </a>
            <a href="tel:9288009850" className="pm-mobile-contact-item">
              <i className="fa-solid fa-phone"></i> +92 (8800) - 9850
            </a>
            <div className="pm-mobile-socials">
              <a href="#" aria-label="Twitter"><i className="fab fa-twitter"></i></a>
              <a href="#" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
              <a href="#" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
              <a href="#" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
            </div>
          </div>
        </div>
      </div>

      <a
        href="#top"
        className="scroll-to-target scroll-to-top"
        style={{
          display: 'none',
          position: 'fixed',
          bottom: '235px',
          right: '35px',
          zIndex: 998,
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
          transition: 'bottom 0.3s ease, opacity 0.3s ease'
        }}
        aria-label="Scroll to top"
      >
        <i className="icon-up-arrow"></i>
      </a>
      <style>{`
        body:has(.mascot-container) .scroll-to-top {
          bottom: 235px !important;
        }
      `}</style>
    </>
  );
}

export default Header;
